var _dec, _dec2, _dec3, _class, _RadioInputGroup;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

/** @jsx jsx */
import React, { Children, Component } from 'react';
import { FormFieldGroup } from '@instructure/ui-form-field';
import { testable } from '@instructure/ui-testable';
import { matchComponentTypes, safeCloneElement, omitProps, pickProps, withDeterministicId } from '@instructure/ui-react-utils';
import { hasVisibleChildren } from '@instructure/ui-a11y-utils';
import { RadioInput } from '../RadioInput';
import { jsx, withStyle } from '@instructure/emotion';
import generateStyle from './styles';
import generateComponentTheme from './theme';
import { allowedProps, propTypes } from './props';
/**
---
category: components
---
**/
let RadioInputGroup = (_dec = withDeterministicId(), _dec2 = withStyle(generateStyle, generateComponentTheme), _dec3 = testable(), _dec(_class = _dec2(_class = _dec3(_class = (_RadioInputGroup = class RadioInputGroup extends Component {
  constructor(props) {
    super(props);
    this.ref = null;
    this._messagesId = void 0;
    this.handleRef = el => {
      this.ref = el;
    };
    this.handleChange = e => {
      const value = e.target.value;
      if (this.props.disabled || this.props.readOnly) {
        e.preventDefault();
        return;
      }
      if (typeof this.props.value === 'undefined') {
        this.setState({
          value
        });
      }
      if (typeof this.props.onChange === 'function') {
        this.props.onChange(e, value);
      }
    };
    if (typeof props.value === 'undefined') {
      this.state = {
        value: props.defaultValue
      };
    }
    this._messagesId = props.deterministicId('RadioInputGroup-messages');
  }
  get hasMessages() {
    return !!this.props.messages && this.props.messages.length > 0;
  }
  get invalid() {
    var _this$props$messages;
    return !!((_this$props$messages = this.props.messages) !== null && _this$props$messages !== void 0 && _this$props$messages.find(m => m.type === 'newError' || m.type === 'error'));
  }
  get value() {
    return typeof this.props.value === 'undefined' ? this.state.value : this.props.value;
  }
  renderChildren() {
    const _this$props = this.props,
      children = _this$props.children,
      name = _this$props.name,
      variant = _this$props.variant,
      size = _this$props.size,
      disabled = _this$props.disabled,
      readOnly = _this$props.readOnly;

    // This adds the passed in name property to each RadioInput component
    // and checks the input whose value matches the value property
    return Children.map(children, (child, index) => {
      if (matchComponentTypes(child, [RadioInput])) {
        const isChecked = this.value === child.props.value;
        const defaultFocus = !this.value && index === 0;
        return safeCloneElement(child, {
          name,
          disabled: disabled || child.props.disabled,
          variant,
          size,
          checked: isChecked,
          onChange: this.handleChange,
          readOnly: readOnly || child.props.readOnly,
          width: child.props.width || 'auto',
          'aria-describedby': this.hasMessages ? this._messagesId : void 0,
          // only one radio in a group should be considered tabbable
          // if a radio is checked, it should be the input to receive focus when tabbed to
          // if none of the inputs are checked, the first should receive the focus
          tabIndex: isChecked || defaultFocus ? 0 : -1,
          label: child.props.label
        });
      } else {
        return child; // ignore (but preserve) children that aren't RadioInput
      }
    });
  }
  render() {
    const _this$props2 = this.props,
      variant = _this$props2.variant,
      layout = _this$props2.layout,
      description = _this$props2.description,
      isRequired = _this$props2.isRequired,
      styles = _this$props2.styles;
    const descriptionWithRequired = hasVisibleChildren(description) ? jsx(React.Fragment, null, description, isRequired && description && jsx("span", {
      css: this.invalid ? styles === null || styles === void 0 ? void 0 : styles.invalidAsterisk : {},
      "aria-hidden": true
    }, ' ', "*")) : description;
    return jsx(FormFieldGroup, Object.assign({}, omitProps(this.props, RadioInputGroup.allowedProps), pickProps(this.props, FormFieldGroup.allowedProps), {
      description: descriptionWithRequired,
      layout: layout === 'columns' && variant === 'toggle' ? 'stacked' : layout // toggles already display in cols
      ,
      vAlign: variant === 'toggle' ? 'middle' : 'top',
      rowSpacing: "small",
      colSpacing: variant === 'toggle' ? 'none' : 'small' // keep toggles close together
      ,
      startAt: variant === 'toggle' ? 'small' : void 0,
      messagesId: this._messagesId,
      elementRef: this.handleRef,
      role: "radiogroup"
    }), this.renderChildren());
  }
}, _RadioInputGroup.displayName = "RadioInputGroup", _RadioInputGroup.componentId = 'RadioInputGroup', _RadioInputGroup.allowedProps = allowedProps, _RadioInputGroup.propTypes = propTypes, _RadioInputGroup.defaultProps = {
  disabled: false,
  variant: 'simple',
  size: 'medium',
  layout: 'stacked',
  readOnly: false
}, _RadioInputGroup)) || _class) || _class) || _class);
export default RadioInputGroup;
export { RadioInputGroup };